import LocationPanels from "./LocationPanels";
import {resolveLocationForDisplay, resolveAllMarkersForDisplay, resolveFloorPlan} from "../util/locationFallback";
import GoogleMapLoader from "./map/GoogleMapLoader";
import RelativeFloorPlan from "./map/RelativeFloorPlan";
import CallHistory from "./CallHistory";
import React, {useCallback, useMemo, useState} from "react";

// renderMap lets the group overview reuse these detail controls while keeping all calls visible.
// Without it, the original single-call map selection and behavior are unchanged.
const Maps = ({topEmlLocation, callUuid, history, renderMap}) => {
    const [alsEnabled, setAlsEnabled] = useState(false);
    const [updateLocations, setUpdateLocations] = useState(true);
    const [showAllLocations, setShowAllLocations] = useState(false);
    const [manualHistoryIndex, setManualHistoryIndex] = useState(null);

    const latestResolvedLocation = useMemo(() => resolveLocationForDisplay(history, 0, alsEnabled), [alsEnabled, history]);
    const latestMarkerToDisplay = useMemo(() => latestResolvedLocation?.marker ?? null, [latestResolvedLocation]);
    const manualMarkerToDisplay = useMemo(() => {
        if (manualHistoryIndex === null) {
            return null;
        }

        return resolveLocationForDisplay(history, manualHistoryIndex, alsEnabled)?.marker ?? null;
    }, [alsEnabled, history, manualHistoryIndex]);
    const allMarkersToDisplay = useMemo(() => resolveAllMarkersForDisplay(history, alsEnabled), [alsEnabled, history]);

    const {isEliOfficeLocation, isTestLocation, isMYLocation, isSbmShipLocation} = useMemo(
        () => resolveFloorPlan(history, topEmlLocation), [history, topEmlLocation]);

    const historyClickHandler = useCallback((historyItemIndex) =>
            // history starts from most recent but one
            setManualHistoryIndex(historyItemIndex + 1),
        []);

    const topHistoryItem = useMemo(() => history.length ? [history[0]] : [], [history]);
    const markerToDisplay = updateLocations
        ? latestMarkerToDisplay
        : (manualHistoryIndex === null ? latestMarkerToDisplay : manualMarkerToDisplay);

    return <div className="call-details">
        <div className="map-panel">
            {renderMap ? renderMap({locationMarker: markerToDisplay, allLocationMarkers: allMarkersToDisplay, showAllLocations, updateLocations, alsEnabled}) : isSbmShipLocation ? <RelativeFloorPlan
                locationMarker={markerToDisplay}
                allLocationMarkers={allMarkersToDisplay}
                showAllLocations={showAllLocations}
                updateLocations={updateLocations}
            /> : <GoogleMapLoader
                isEliOfficeLocation={isEliOfficeLocation}
                isTestLocation={isTestLocation}
                isMYLocation={isMYLocation}
                allLocationMarkers={allMarkersToDisplay}
                locationMarker={markerToDisplay}
                showAllLocations={showAllLocations}
                updateLocations={updateLocations}
            />}
        </div>
        <LocationPanels>
            <section className="location-panel">
                <h2 className="panel-title">Current Location</h2>
                <div className="panel-content"><CallHistory callUuid={callUuid} history={topHistoryItem} clickAllowed={false}/></div>
            </section>
            <section className="history-panel">
                <div className="history-header">
                    <h2 className="panel-title">History <span className="update-count">{history.length} updates</span></h2>
                    <div className="history-toggles" role="group" aria-label="Map controls">
                        <label><input type="checkbox" role="switch" checked={updateLocations}
                            onChange={event => {
                                setUpdateLocations(event.target.checked);
                                if (event.target.checked) setShowAllLocations(false);
                            }}/>Auto refresh</label>
                        <label><input type="checkbox" role="switch" checked={showAllLocations}
                            onChange={event => {
                                setShowAllLocations(event.target.checked);
                                if (event.target.checked) setUpdateLocations(false);
                            }}/>Show All</label>
                        <label><input type="checkbox" role="switch" checked={alsEnabled}
                            onChange={event => setAlsEnabled(event.target.checked)}/>ALS</label>
                    </div>
                </div>
                <div className="panel-content"><CallHistory clickHandler={historyClickHandler}
                    clickAllowed={!updateLocations} callUuid={callUuid} history={history.slice(1)}/></div>
            </section>
        </LocationPanels>
    </div>
};

export default Maps;
